import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * The charts the reports draw (ADR-074), in SVG and plain HTML — no chart library.
 *
 * The rules they follow are the dataviz ones, and they are easy to break at a call site, so they
 * live here rather than in each report:
 *
 * - **A colour is a slot, and a slot is what a series IS.** `slot` picks one of the eight
 *   `--color-chart-*` tokens (index.css) in their fixed, colour-blind-checked order. A legend
 *   that hides a series never repaints the others.
 * - **One axis.** Nothing here draws two y-scales; two measures are two charts.
 * - **Thin marks.** Columns at most 24px wide with a 4px rounded data end and a square baseline,
 *   2px lines, a 2px surface gap between stacked segments, a 2px surface ring on dots.
 * - **Hover and keyboard both work.** A column chart's band, a line chart's X, a bar's row and a
 *   heat cell each carry a tooltip; the plot takes focus and the arrow keys walk it. A tooltip
 *   never holds the only copy of a value: every chart has a table beside it on its page.
 * - **Text wears text tokens**, never the series colour; a swatch beside it carries identity.
 */

export type Slot = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | "other";

export function slotColor(slot: Slot): string {
  return slot === "other" ? "var(--color-chart-other)" : `var(--color-chart-${slot})`;
}

export interface SeriesDef {
  key: string;
  label: string;
  slot: Slot;
  /** A colour of the design system's own instead of the slot's, for a series that means the same
   *  thing on every screen (an outcome group, `outcomeColor`). Legend, bar list and readout. */
  color?: string;
}

function seriesColor(s: { slot?: Slot; color?: string }): string {
  return s.color ?? slotColor(s.slot ?? "other");
}

export interface ChartDatum {
  key: string;
  /** What the axis prints: short. */
  label: string;
  /** What the tooltip heads with: may be longer than the axis has room for. */
  title?: string;
  values: Record<string, number | null | undefined>;
}

// --- measuring and scaling ---------------------------------------------------------------------

/** The element's width, following it as the layout changes. 0 until it is on the page. */
export function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = React.useRef<T | null>(null);
  const [width, setWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(Math.floor(el.getBoundingClientRect().width));
    const observer = new ResizeObserver((entries) => {
      const next = Math.floor(entries[0]?.contentRect.width ?? 0);
      setWidth((prev) => (prev === next ? prev : next));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** The smallest "round" number at or above `max`: 1, 2, 2.5 or 5 times a power of ten. */
export function niceMax(max: number): number {
  if (!Number.isFinite(max) || max <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(max));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (step * power >= max) return step * power;
  }
  return 10 * power;
}

/** Four even steps from 0 to `top`, all of them clean numbers when `top` is `niceMax`'s. */
export function ticksFor(top: number, count = 4): number[] {
  return Array.from({ length: count + 1 }, (_, i) => (top / count) * i);
}

function shownSeries(series: SeriesDef[], hidden: ReadonlySet<string>): SeriesDef[] {
  const shown = series.filter((s) => !hidden.has(s.key));
  return shown.length > 0 ? shown : series;
}

/** Every `step`-th label, so the axis never prints two on top of each other. */
function labelStep(count: number, band: number, longest: number): number {
  const needed = longest * 6.2 + 12;
  return Math.max(1, Math.ceil(needed / Math.max(band, 1)));
}

// --- the pieces around a plot ------------------------------------------------------------------

export function Legend({
  series, hidden, onToggle, shape = "rect", className,
}: {
  series: SeriesDef[];
  hidden?: ReadonlySet<string>;
  /** Click to hide or show a series; omit for a legend that only names. */
  onToggle?: (key: string) => void;
  shape?: "rect" | "line";
  className?: string;
}) {
  if (series.length < 2) return null;
  return (
    <ul className={cn("flex flex-wrap items-center gap-x-4 gap-y-1", className)}>
      {series.map((s) => {
        const off = hidden?.has(s.key) ?? false;
        const key = (
          <>
            <span
              aria-hidden
              className={cn("inline-block shrink-0", shape === "line" ? "h-0.5 w-3 rounded" : "size-2.5 rounded-[3px]")}
              style={{ background: seriesColor(s), opacity: off ? 0.3 : 1 }}
            />
            <span className={cn("text-xs", off ? "text-text-subtle line-through" : "text-text-muted")}>{s.label}</span>
          </>
        );
        return (
          <li key={s.key}>
            {onToggle ? (
              <button
                type="button"
                onClick={() => onToggle(s.key)}
                aria-pressed={!off}
                className="inline-flex items-center gap-1.5 rounded px-0.5 hover:text-text"
                title={off ? `Show ${s.label}` : `Hide ${s.label}`}
              >
                {key}
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5">{key}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export interface TipRow {
  key: string;
  label: string;
  value: string;
  slot?: Slot;
  color?: string;
  shape?: "rect" | "line";
}

/** The readout: values lead, labels follow; a short key of the series colour beside each. */
function TipBox({
  left, top, containerWidth, title, rows, footer,
}: {
  left: number;
  top: number;
  containerWidth: number;
  title?: string;
  rows: TipRow[];
  footer?: string;
}) {
  const width = 188;
  const x = left + 12 + width > containerWidth ? Math.max(0, left - width - 12) : left + 12;
  return (
    <div
      role="presentation"
      className="pointer-events-none absolute z-20 rounded-md border border-border bg-surface-raised px-2.5 py-2 text-xs shadow-md"
      style={{ left: x, top, width }}
    >
      {title && <p className="mb-1 truncate font-medium text-text">{title}</p>}
      <ul className="space-y-0.5">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center gap-2">
            {(r.slot !== undefined || r.color !== undefined) && (
              <span
                aria-hidden
                className={cn("inline-block shrink-0", r.shape === "rect" ? "size-2 rounded-[2px]" : "h-0.5 w-2.5 rounded")}
                style={{ background: seriesColor(r) }}
              />
            )}
            <span className="font-semibold tabular-nums text-text">{r.value}</span>
            <span className="min-w-0 truncate text-text-muted">{r.label}</span>
          </li>
        ))}
      </ul>
      {footer && <p className="mt-1 border-t border-border pt-1 text-text-muted">{footer}</p>}
    </div>
  );
}

export function ChartEmpty({ height = 160, children }: { height?: number; children?: React.ReactNode }) {
  return (
    <div
      className="flex items-center justify-center rounded-md border border-dashed border-border text-xs text-text-subtle"
      style={{ height }}
    >
      {children ?? "Nothing in this window"}
    </div>
  );
}

// --- columns -----------------------------------------------------------------------------------

function roundedTop(x: number, y: number, w: number, h: number, r: number): string {
  const radius = Math.max(0, Math.min(r, w / 2, h));
  return [
    `M${x},${y + h}`,
    `L${x},${y + radius}`,
    `Q${x},${y} ${x + radius},${y}`,
    `L${x + w - radius},${y}`,
    `Q${x + w},${y} ${x + w},${y + radius}`,
    `L${x + w},${y + h}`,
    "Z",
  ].join(" ");
}

/**
 * Columns over a sequence — time buckets, or bins — stacked when there is more than one series.
 * The band under the pointer is the hit target, not the painted pixels, and the tooltip reads
 * every series at that band.
 */
export function ColumnChart({
  data, series, height = 220, format, axisFormat, ariaLabel, total = true, onSelect,
}: {
  data: ChartDatum[];
  series: SeriesDef[];
  height?: number;
  format: (value: number) => string;
  axisFormat?: (value: number) => string;
  ariaLabel: string;
  /** Show the stack's total in the tooltip. */
  total?: boolean;
  /** A click on a band: drill into it. */
  onSelect?: (datum: ChartDatum) => void;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hidden, setHidden] = React.useState<ReadonlySet<string>>(new Set());
  const [active, setActive] = React.useState<number | null>(null);
  const shown = shownSeries(series, hidden);
  const toggle = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const sums = data.map((d) => shown.reduce((acc, s) => acc + (d.values[s.key] ?? 0), 0));
  const top = niceMax(Math.max(0, ...sums));
  const ticks = ticksFor(top);
  const fmtAxis = axisFormat ?? format;
  const left = Math.max(28, ...ticks.map((t) => fmtAxis(t).length * 6.2 + 10));
  const margin = { top: 8, right: 4, bottom: 22, left };
  const plotW = Math.max(0, width - margin.left - margin.right);
  const plotH = Math.max(0, height - margin.top - margin.bottom);
  const band = data.length > 0 ? plotW / data.length : 0;
  const barW = Math.max(1, Math.min(24, band * 0.72));
  const y = (v: number) => plotH - (v / top) * plotH;
  const step = labelStep(data.length, band, Math.max(0, ...data.map((d) => d.label.length)));

  const move = (delta: number) =>
    setActive((prev) => {
      if (data.length === 0) return null;
      const from = prev ?? (delta > 0 ? -1 : data.length);
      return Math.min(data.length - 1, Math.max(0, from + delta));
    });

  const current = active !== null ? data[active] : undefined;
  return (
    <div className="space-y-2">
      <Legend series={series} hidden={hidden} onToggle={toggle} />
      <div ref={ref} className="relative" style={{ height }}>
        {width > 0 && (
          <svg
            width={width}
            height={height}
            role="img"
            aria-label={ariaLabel}
            tabIndex={0}
            className="block outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") { e.preventDefault(); move(1); }
              else if (e.key === "ArrowLeft") { e.preventDefault(); move(-1); }
              else if (e.key === "Enter" && current && onSelect) onSelect(current);
              else if (e.key === "Escape") setActive(null);
            }}
            onBlur={() => setActive(null)}
            onPointerLeave={() => setActive(null)}
          >
            <g transform={`translate(${margin.left},${margin.top})`}>
              {ticks.map((t) => (
                <g key={t} transform={`translate(0,${y(t)})`}>
                  <line x1={0} x2={plotW} stroke="var(--color-border)" strokeWidth={1} shapeRendering="crispEdges" />
                  <text x={-6} dy="0.32em" textAnchor="end" fontSize={11} fill="var(--color-text-subtle)" className="tabular-nums">
                    {fmtAxis(t)}
                  </text>
                </g>
              ))}
              {active !== null && (
                <rect x={active * band} y={0} width={band} height={plotH} fill="var(--color-surface-hover)" />
              )}
              {data.map((d, i) => {
                const x = i * band + (band - barW) / 2;
                let cum = 0;
                const segments = shown
                  .map((s) => ({ s, v: Math.max(0, d.values[s.key] ?? 0) }))
                  .filter(({ v }) => v > 0);
                return (
                  <g key={d.key}>
                    {segments.map(({ s, v }, idx) => {
                      const y0 = y(cum);
                      cum += v;
                      const y1 = y(cum);
                      const gap = idx > 0 ? 2 : 0;
                      const h = Math.max(0, y0 - y1 - gap);
                      if (h <= 0) return null;
                      const isTop = idx === segments.length - 1;
                      return isTop ? (
                        <path key={s.key} d={roundedTop(x, y1, barW, h, 4)} fill={slotColor(s.slot)} />
                      ) : (
                        <rect key={s.key} x={x} y={y1} width={barW} height={h} fill={slotColor(s.slot)} />
                      );
                    })}
                  </g>
                );
              })}
              <line x1={0} x2={plotW} y1={plotH} y2={plotH} stroke="var(--color-border-strong)" strokeWidth={1} shapeRendering="crispEdges" />
              {data.map((d, i) =>
                i % step === 0 ? (
                  <text
                    key={d.key}
                    x={i * band + band / 2}
                    y={plotH + 15}
                    textAnchor="middle"
                    fontSize={11}
                    fill="var(--color-text-subtle)"
                  >
                    {d.label}
                  </text>
                ) : null,
              )}
              {data.map((d, i) => (
                <rect
                  key={d.key}
                  x={i * band}
                  y={0}
                  width={band}
                  height={plotH}
                  fill="transparent"
                  style={{ cursor: onSelect ? "pointer" : "default" }}
                  onPointerEnter={() => setActive(i)}
                  onClick={() => onSelect?.(d)}
                />
              ))}
            </g>
          </svg>
        )}
        {current && active !== null && (
          <TipBox
            left={margin.left + active * band + band / 2}
            top={margin.top}
            containerWidth={width}
            title={current.title ?? current.label}
            rows={shown.map((s) => ({
              key: s.key,
              label: s.label,
              value: format(current.values[s.key] ?? 0),
              slot: series.length > 1 ? s.slot : undefined,
              shape: "rect" as const,
            }))}
            footer={total && shown.length > 1 ? `${format(sums[active] ?? 0)} in all` : undefined}
          />
        )}
      </div>
    </div>
  );
}

// --- lines -------------------------------------------------------------------------------------

/**
 * One to three lines over the same sequence as a column chart above it, so the two line up. A
 * rate is drawn on a fixed 0–100% axis (`percent`), which is the only honest scale for one; a
 * missing value (a rate over nothing) breaks the line rather than dropping it to zero.
 */
export function LineChart({
  data, series, height = 180, format, axisFormat, ariaLabel, percent = false, area = false,
}: {
  data: ChartDatum[];
  series: SeriesDef[];
  height?: number;
  format: (value: number) => string;
  axisFormat?: (value: number) => string;
  ariaLabel: string;
  percent?: boolean;
  /** A 10% wash under a single series. */
  area?: boolean;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hidden, setHidden] = React.useState<ReadonlySet<string>>(new Set());
  const [active, setActive] = React.useState<number | null>(null);
  const shown = shownSeries(series, hidden);
  const toggle = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const values = data.flatMap((d) => shown.map((s) => d.values[s.key]).filter((v): v is number => v != null));
  const top = percent ? 1 : niceMax(Math.max(0, ...values));
  const ticks = ticksFor(top);
  const fmtAxis = axisFormat ?? format;
  const left = Math.max(28, ...ticks.map((t) => fmtAxis(t).length * 6.2 + 10));
  const margin = { top: 10, right: 8, bottom: 22, left };
  const plotW = Math.max(0, width - margin.left - margin.right);
  const plotH = Math.max(0, height - margin.top - margin.bottom);
  const band = data.length > 0 ? plotW / data.length : 0;
  const x = (i: number) => i * band + band / 2;
  const y = (v: number) => plotH - (Math.min(v, top) / top) * plotH;
  const step = labelStep(data.length, band, Math.max(0, ...data.map((d) => d.label.length)));

  const pathFor = (key: string): string => {
    let d = "";
    let pen = false;
    data.forEach((row, i) => {
      const v = row.values[key];
      if (v == null) {
        pen = false;
        return;
      }
      d += `${pen ? "L" : "M"}${x(i)},${y(v)} `;
      pen = true;
    });
    return d.trim();
  };
  const lastOf = (key: string): number | null => {
    for (let i = data.length - 1; i >= 0; i -= 1) if (data[i]?.values[key] != null) return i;
    return null;
  };
  const move = (delta: number) =>
    setActive((prev) => {
      if (data.length === 0) return null;
      const from = prev ?? (delta > 0 ? -1 : data.length);
      return Math.min(data.length - 1, Math.max(0, from + delta));
    });
  const current = active !== null ? data[active] : undefined;

  return (
    <div className="space-y-2">
      <Legend series={series} hidden={hidden} onToggle={toggle} shape="line" />
      <div ref={ref} className="relative" style={{ height }}>
        {width > 0 && (
          <svg
            width={width}
            height={height}
            role="img"
            aria-label={ariaLabel}
            tabIndex={0}
            className="block outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") { e.preventDefault(); move(1); }
              else if (e.key === "ArrowLeft") { e.preventDefault(); move(-1); }
              else if (e.key === "Escape") setActive(null);
            }}
            onBlur={() => setActive(null)}
            onPointerLeave={() => setActive(null)}
            onPointerMove={(e) => {
              const box = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
              const px = e.clientX - box.left - margin.left;
              if (band <= 0) return;
              setActive(Math.min(data.length - 1, Math.max(0, Math.floor(px / band))));
            }}
          >
            <g transform={`translate(${margin.left},${margin.top})`}>
              {ticks.map((t) => (
                <g key={t} transform={`translate(0,${y(t)})`}>
                  <line x1={0} x2={plotW} stroke="var(--color-border)" strokeWidth={1} shapeRendering="crispEdges" />
                  <text x={-6} dy="0.32em" textAnchor="end" fontSize={11} fill="var(--color-text-subtle)" className="tabular-nums">
                    {fmtAxis(t)}
                  </text>
                </g>
              ))}
              {area && shown.length === 1 && shown[0] && (
                <path
                  d={`${pathFor(shown[0].key)} L${x(lastOf(shown[0].key) ?? 0)},${plotH} L${x(0)},${plotH} Z`}
                  fill={slotColor(shown[0].slot)}
                  opacity={0.1}
                />
              )}
              {shown.map((s) => (
                <path
                  key={s.key}
                  d={pathFor(s.key)}
                  fill="none"
                  stroke={slotColor(s.slot)}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ))}
              {active !== null && (
                <line x1={x(active)} x2={x(active)} y1={0} y2={plotH} stroke="var(--color-border-strong)" strokeWidth={1} />
              )}
              {shown.map((s) => {
                const i = active ?? lastOf(s.key);
                const v = i !== null ? data[i]?.values[s.key] : null;
                if (i === null || v == null) return null;
                return (
                  <circle key={s.key} cx={x(i)} cy={y(v)} r={4} fill={slotColor(s.slot)} stroke="var(--color-surface)" strokeWidth={2} />
                );
              })}
              <line x1={0} x2={plotW} y1={plotH} y2={plotH} stroke="var(--color-border-strong)" strokeWidth={1} shapeRendering="crispEdges" />
              {data.map((d, i) =>
                i % step === 0 ? (
                  <text key={d.key} x={x(i)} y={plotH + 15} textAnchor="middle" fontSize={11} fill="var(--color-text-subtle)">
                    {d.label}
                  </text>
                ) : null,
              )}
            </g>
          </svg>
        )}
        {current && active !== null && (
          <TipBox
            left={margin.left + x(active)}
            top={margin.top}
            containerWidth={width}
            title={current.title ?? current.label}
            rows={shown.map((s) => {
              const v = current.values[s.key];
              return {
                key: s.key,
                label: s.label,
                value: v == null ? "—" : format(v),
                slot: series.length > 1 ? s.slot : undefined,
                shape: "line" as const,
              };
            })}
          />
        )}
      </div>
    </div>
  );
}

// --- horizontal bars ---------------------------------------------------------------------------

export interface BarRow {
  key: string;
  label: React.ReactNode;
  /** A plain-text name for the tooltip and the screen reader. */
  name: string;
  sublabel?: React.ReactNode;
  values: Record<string, number>;
  /** What prints at the bar's end; default the formatted total. */
  valueLabel?: string;
}

/**
 * A ranked list: one bar per row, a label before it and its value after, stacked by series when
 * there is more than one. Rows are HTML, so long names wrap and truncate like everywhere else.
 */
export function BarList({
  rows, series, format, max, onSelect, ariaLabel, barHeight = 12, limit = 12,
}: {
  rows: BarRow[];
  series: SeriesDef[];
  format: (value: number) => string;
  /** The value a full-width bar stands for; default the largest row. */
  max?: number;
  onSelect?: (row: BarRow) => void;
  ariaLabel: string;
  barHeight?: number;
  /** Rows shown before "Show all". */
  limit?: number;
}) {
  const [hidden, setHidden] = React.useState<ReadonlySet<string>>(new Set());
  const [active, setActive] = React.useState<string | null>(null);
  const [all, setAll] = React.useState(false);
  const shown = shownSeries(series, hidden);
  const toggle = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const totals = rows.map((r) => shown.reduce((acc, s) => acc + (r.values[s.key] ?? 0), 0));
  const top = max ?? Math.max(0, ...totals);

  return (
    <div className="space-y-2">
      <Legend series={series} hidden={hidden} onToggle={toggle} />
      <ul aria-label={ariaLabel} className="space-y-1.5">
        {(all ? rows : rows.slice(0, limit)).map((r, i) => {
          const sum = totals[i] ?? 0;
          const parts = shown.map((s) => ({ s, v: Math.max(0, r.values[s.key] ?? 0) })).filter(({ v }) => v > 0);
          const Row = onSelect ? "button" : "div";
          return (
            <li key={r.key} className="relative">
              <Row
                {...(onSelect ? { type: "button" as const, onClick: () => onSelect(r) } : {})}
                onPointerEnter={() => setActive(r.key)}
                onPointerLeave={() => setActive(null)}
                onFocus={() => setActive(r.key)}
                onBlur={() => setActive(null)}
                tabIndex={onSelect ? undefined : 0}
                className={cn(
                  "grid w-full grid-cols-[minmax(7rem,14rem)_minmax(0,1fr)_4.5rem] items-center gap-3 rounded px-1 py-0.5 text-left",
                  "outline-none focus-visible:outline-2 focus-visible:outline-focus",
                  active === r.key && "bg-surface-hover",
                  onSelect && "cursor-pointer",
                )}
              >
                <span className="min-w-0" title={r.name}>
                  <span className="block truncate text-xs text-text">{r.label}</span>
                  {r.sublabel && <span className="block truncate text-2xs text-text-subtle">{r.sublabel}</span>}
                </span>
                <span className="flex h-full items-center">
                  <span
                    className="flex gap-[2px]"
                    style={{ width: top > 0 ? `${Math.max(sum > 0 ? 1 : 0, (sum / top) * 100)}%` : "0%", height: barHeight }}
                  >
                    {parts.map(({ s, v }, idx) => (
                      <span
                        key={s.key}
                        className={cn("h-full", idx === parts.length - 1 && "rounded-r-[4px]")}
                        style={{ width: `${(v / sum) * 100}%`, background: seriesColor(s), minWidth: 1 }}
                      />
                    ))}
                  </span>
                </span>
                <span className="text-right text-xs font-medium tabular-nums text-text">{r.valueLabel ?? format(sum)}</span>
              </Row>
              {active === r.key && shown.length > 1 && (
                <div className="pointer-events-none absolute right-20 top-full z-20 mt-1">
                  <div className="relative">
                    <TipBox
                      left={0}
                      top={0}
                      containerWidth={400}
                      title={r.name}
                      rows={shown.map((s) => ({
                        key: s.key,
                        label: s.label,
                        value: format(r.values[s.key] ?? 0),
                        slot: s.slot,
                        color: s.color,
                        shape: "rect" as const,
                      }))}
                    />
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {rows.length > limit && (
        <button type="button" className="text-xs text-accent-11 hover:underline" onClick={() => setAll((v) => !v)}>
          {all ? `Show the top ${limit}` : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}

// --- the weekday x hour heatmap ----------------------------------------------------------------

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
const SEQ_STEPS = 7;

export interface HeatCell {
  /** ISO weekday, 1 = Monday. */
  weekday: number;
  hour: number;
  value: number | null;
  /** More about the cell, for the tooltip: "12 calls · 83% answered". */
  detail?: string;
}

function hourLabel(hour: number): string {
  if (hour === 0) return "12a";
  if (hour === 12) return "12p";
  return hour < 12 ? `${hour}a` : `${hour - 12}p`;
}

/** Which of the seven ramp steps a value takes; null or zero is none (the surface). */
export function seqStep(value: number | null, max: number): number {
  if (value == null || value <= 0 || max <= 0) return 0;
  return Math.min(SEQ_STEPS, Math.max(1, Math.ceil((value / max) * SEQ_STEPS)));
}

/**
 * When things happen: a week of hours on the reader's clock, darker where there is more. One hue
 * (`--color-seq-*`), because the job is magnitude; empty cells are the surface itself.
 */
export function Heatmap({
  cells, format, ariaLabel, legend,
}: {
  cells: HeatCell[];
  format: (value: number) => string;
  ariaLabel: string;
  /** What the darker end means, for the scale beside it: "More calls". */
  legend: string;
}) {
  const [active, setActive] = React.useState<string | null>(null);
  const byKey = new Map(cells.map((c) => [`${c.weekday}:${c.hour}`, c]));
  const max = Math.max(0, ...cells.map((c) => c.value ?? 0));
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const activeCell = active ? byKey.get(active) : undefined;
  return (
    <div className="space-y-2">
      <div role="grid" aria-label={ariaLabel} className="overflow-x-auto scrollbar-subtle">
        <div className="grid min-w-[36rem] grid-cols-[2.5rem_repeat(24,minmax(0,1fr))] gap-[2px]">
          <span />
          {hours.map((h) => (
            <span key={h} className="text-center text-2xs text-text-subtle">
              {h % 3 === 0 ? hourLabel(h) : ""}
            </span>
          ))}
          {WEEKDAYS.map((name, idx) => {
            const weekday = idx + 1;
            return (
              <React.Fragment key={name}>
                <span role="rowheader" className="self-center text-2xs text-text-subtle">
                  {name}
                </span>
                {hours.map((h) => {
                  const key = `${weekday}:${h}`;
                  const cell = byKey.get(key);
                  const stepN = seqStep(cell?.value ?? null, max);
                  const text = `${name} ${hourLabel(h)}: ${cell?.value != null ? format(cell.value) : "none"}${cell?.detail ? ` · ${cell.detail}` : ""}`;
                  return (
                    <span
                      key={key}
                      role="gridcell"
                      tabIndex={cell ? 0 : -1}
                      aria-label={text}
                      onPointerEnter={() => setActive(key)}
                      onPointerLeave={() => setActive(null)}
                      onFocus={() => setActive(key)}
                      onBlur={() => setActive(null)}
                      className={cn(
                        "h-5 rounded-[3px] outline-none focus-visible:outline-2 focus-visible:outline-focus",
                        active === key && "ring-2 ring-text/40",
                      )}
                      style={{ background: stepN === 0 ? "var(--color-surface-sunken)" : `var(--color-seq-${stepN})` }}
                    />
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-2xs text-text-subtle">
        <span className="min-h-4 text-xs text-text-muted">
          {activeCell
            ? `${WEEKDAYS[activeCell.weekday - 1]} ${hourLabel(activeCell.hour)}: ${activeCell.value != null ? format(activeCell.value) : "none"}${activeCell.detail ? ` · ${activeCell.detail}` : ""}`
            : "Hover a cell for its numbers"}
        </span>
        <span className="flex items-center gap-1.5">
          <span>Less</span>
          {Array.from({ length: SEQ_STEPS }, (_, i) => (
            <span key={i} className="size-3 rounded-[2px]" style={{ background: `var(--color-seq-${i + 1})` }} />
          ))}
          <span>{legend}</span>
        </span>
      </div>
    </div>
  );
}

// --- small figures -----------------------------------------------------------------------------

/** A trend in a stat tile: de-emphasised, with the latest point in the accent. */
export function Sparkline({ values, height = 28, className }: { values: (number | null)[]; height?: number; className?: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const present = values.filter((v): v is number => v != null);
  const top = Math.max(0, ...present);
  const x = (i: number) => (values.length > 1 ? (i / (values.length - 1)) * (width - 4) + 2 : width / 2);
  const y = (v: number) => height - 3 - (top > 0 ? (v / top) * (height - 6) : 0);
  let d = "";
  let pen = false;
  values.forEach((v, i) => {
    if (v == null) {
      pen = false;
      return;
    }
    d += `${pen ? "L" : "M"}${x(i)},${y(v)} `;
    pen = true;
  });
  let last: number | null = null;
  for (let i = values.length - 1; i >= 0; i -= 1) {
    if (values[i] != null) {
      last = i;
      break;
    }
  }
  return (
    <div ref={ref} className={cn("h-7 w-full", className)} aria-hidden>
      {width > 0 && present.length > 1 && (
        <svg width={width} height={height}>
          <path d={d.trim()} fill="none" stroke="var(--color-text-subtle)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
          {last !== null && values[last] != null && (
            <circle cx={x(last)} cy={y(values[last]!)} r={2.5} fill="var(--color-chart-1)" />
          )}
        </svg>
      )}
    </div>
  );
}

/**
 * A single ratio against a limit: the fill in the accent, or the warning or danger ramp when the
 * value crosses `warnAt` / `dangerAt`; the unfilled track a lighter step. The limit is marked.
 */
export function Meter({
  value, max = 1, warnAt, dangerAt, limit, label,
}: {
  value: number | null;
  max?: number;
  warnAt?: number;
  dangerAt?: number;
  /** Draws a tick where the rule's line is. */
  limit?: number;
  label: string;
}) {
  const share = value == null ? 0 : Math.min(1, Math.max(0, value / max));
  const tone =
    value != null && dangerAt !== undefined && value >= dangerAt
      ? "var(--color-danger-9)"
      : value != null && warnAt !== undefined && value >= warnAt
        ? "var(--color-warning-9)"
        : "var(--color-chart-1)";
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value ?? undefined}
      className="relative h-1.5 w-full rounded-full bg-gray-4"
    >
      <div className="h-full rounded-full" style={{ width: `${share * 100}%`, background: tone }} />
      {limit !== undefined && (
        <span
          aria-hidden
          className="absolute -top-0.5 h-2.5 w-px bg-text-muted"
          style={{ left: `${Math.min(100, (limit / max) * 100)}%` }}
        />
      )}
    </div>
  );
}
